
import * as React from 'react';
import {
    DetailsList,
    IDetailsListProps,
    IDetailsHeaderProps,
    ConstrainMode,
    DetailsListLayoutMode
} from 'office-ui-fabric-react/lib/DetailsList';
import {
    ISelectionZoneProps
} from 'office-ui-fabric-react/lib/Selection';
import {
    MarqueeSelection
} from 'office-ui-fabric-react/lib/MarqueeSelection';
import {
    IRenderFunction
} from 'office-ui-fabric-react/lib/Utilities';
import {
    withColumnSelector, IDecorateWithColumnSelectorProps
} from '../decorators/withColumnSelector';
import { withCommandColumns, ICommandColumnsProps } from '../decorators/withCommandColumns';
import {
    withCachedScrollPosition, IDecorateWithCachedScrollPositionProps, IWithCachedScrollPositionProps
} from '../decorators/withCachedScrollPosition';
import {
    withItemSetRowsAndGroups, IDecorateWithItemSetRowsAndGroupsProps, IOverrideDetailsListProps
} from './decorators/withItemSetRowsAndGroups';
import {
    withLayoutRenderComplete, IDecorateWithLayoutRenderCompleteProps
} from '../decorators/withLayoutRenderComplete';
import { injectProps, wrapWithParent } from '../../utilities/components/Props';
import strings from './DetailsList.resx';
import { IDecoratedProps, withDisplayName } from '../../utilities/components/Decorators';
import { withDetailsListShimmer } from './decorators/withDetailsListShimmer';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { isPageLoaded } from '../../operations/pageLoad';
import { IItemColumn } from '../../models/schema/IItemColumn';
import { areShallowDifferent } from '@ms/utilities-objects/lib/areShallowEqual';

// Bind the commands specified in the columns to context menus.
const WithCommandColumnsDetailsHeader = withDisplayName('CommandColumnsDetailsHeader')(connectToItemCache((
    itemCache: IItemCache,
    props: Pick<ICommandColumnsProps, 'columns'>): Pick<ICommandColumnsProps, 'columns'> => {
    const pageLoaded = isPageLoaded(itemCache);

    if (!pageLoaded && props.columns) {
        // If the page has not yet loaded, blank out the column headers to avoid visual inconsistency.
        return {
            columns: props.columns.map((column: IItemColumn): IItemColumn => {
                const {
                    name,
                    iconName,
                    iconClassName,
                    headerCommand,
                    onColumnClick,
                    onColumnContextMenu,
                    columnActionsMode,
                    ...newColumn
                } = column;

                return {
                    ...newColumn,
                    name: ''
                };
            })
        };
    }

    return {
        columns: props.columns
    };
})(withDisplayName('CommandColumnsDetailsHeader.Inner')(withCommandColumns()((
    props: IDetailsHeaderProps & {
        defaultRender: IRenderFunction<IDetailsHeaderProps>
    }
) => {
    const {
        defaultRender,
        ...detailsHeaderProps
    } = props;

    return (
        defaultRender(detailsHeaderProps)
    );
}))));

const renderDetailsHeader = (
    detailsHeaderProps?: IDetailsHeaderProps,
    defaultRender?: IRenderFunction<IDetailsHeaderProps>
): JSX.Element | null => {
    return (
        detailsHeaderProps && defaultRender ? (
            <WithCommandColumnsDetailsHeader
                { ...detailsHeaderProps }
                defaultRender={ defaultRender }
            />
        ) : null
    );
};

export type IItemSetDetailsListInjectedProps = Pick<IDetailsListProps,
    'initialFocusedIndex' |
    'onRenderDetailsHeader'>;

export type IItemSetDetailsListProps = IDecorateWithColumnSelectorProps<
    IDecorateWithItemSetRowsAndGroupsProps<
    IDecorateWithLayoutRenderCompleteProps<
    IDecorateWithCachedScrollPositionProps<
    IDecoratedProps<IDetailsListProps, Pick<IDetailsListProps, never>, IItemSetDetailsListInjectedProps & IOverrideDetailsListProps>
    >
    >
    >
    >;

/**
 * Add Shimmer around a standard DetailsList.
 */
const ShimmeredDetailsList = withDetailsListShimmer()(DetailsList);

/**
 * Add accessibility and render cleanup to the DetailsList.
 */
const AccessibleDetailsList: React.ComponentType<IDetailsListProps & IOverrideDetailsListProps> = injectProps(() => {
    let previousSelectionZoneProps: ISelectionZoneProps | undefined;

    return (props: IDetailsListProps & IOverrideDetailsListProps): Partial<IDetailsListProps> => {
        const {
            detailsListOverrides,
            selection,
            selectionZoneProps: originalSelectionZoneProps,
            ...detailsListProps
        } = props;

        const selectionZoneProps = originalSelectionZoneProps || selection ? {
            ...(originalSelectionZoneProps || {}),
            ...(selection ? {
                selection,
                disableAutoSelectOnInputElements: true
            } : {})
        } as ISelectionZoneProps : undefined;

        const finalSelectionZoneProps =
            !previousSelectionZoneProps || areShallowDifferent(previousSelectionZoneProps, selectionZoneProps) ?
                selectionZoneProps :
                previousSelectionZoneProps;

        previousSelectionZoneProps = finalSelectionZoneProps;

        return {
            // Add default behaviors for the DetailsList here.
            ariaLabelForGrid: strings.listTooltip,
            ariaLabelForSelectionColumn: strings.selectionColumnHeaderAriaLabel,
            ariaLabelForSelectAllCheckbox: strings.toggleSelectAllAriaLabel,
            ariaLabelForListHeader: strings.ColumnHeaderOperationAriaLabel,
            constrainMode: ConstrainMode.unconstrained,
            layoutMode: DetailsListLayoutMode.justified,
            selection,
            // These are behaviors supplied by control handlers.
            ...detailsListOverrides,
            // Past this point are behaviors supplied by the decorators and the host application.
            ...detailsListProps,
            selectionZoneProps: finalSelectionZoneProps
        };
    };
})(ShimmeredDetailsList);

/**
 * Wrap the details list with marquee selection.
 */
const MarqueeDetailsList = wrapWithParent((props: Pick<IDetailsListProps, 'selection'> & {
    disableMarqueeSelection?: boolean;
} & Pick<React.Props<{}>, 'children'>): JSX.Element => {
    const { selection, disableMarqueeSelection } = props;

    return selection && !disableMarqueeSelection ? (
        <MarqueeSelection selection={ selection }>
            { props.children }
        </MarqueeSelection>
    ) : React.Children.only(props.children);
})(AccessibleDetailsList);

function composeRenderFunctions<TProps>(outer: IRenderFunction<TProps>, inner: IRenderFunction<TProps>): IRenderFunction<TProps> {
    // TODO Memoize this and move this to Fabric.
    return (
        props?: TProps,
        defaultRender?: IRenderFunction<TProps>
    ): JSX.Element | null => {
        return outer(props, (innerProps?: TProps) => {
            return inner(innerProps, defaultRender);
        });
    };
}

/**
 * Show a context menu when column headers are clicked.
 */
const CommandHeaderDetailsList = withDisplayName('DetailsListWithCommandHeaders')(injectProps((
    props: Pick<IDetailsListProps, 'onRenderDetailsHeader'>
): IItemSetDetailsListInjectedProps => {
    const {
        onRenderDetailsHeader
    } = props;

    return {
        onRenderDetailsHeader: onRenderDetailsHeader ?
            composeRenderFunctions(onRenderDetailsHeader, renderDetailsHeader) :
            renderDetailsHeader
    };
})(MarqueeDetailsList));

/**
 * Remember the last-saved scroll position for the current item.
 */
const ScrollPositionDetailsList: React.ComponentType<IDetailsListProps & IWithCachedScrollPositionProps & IOverrideDetailsListProps> =
    withCachedScrollPosition()(CommandHeaderDetailsList);

/**
 * Bubble up an event for each layout re-render.
 */
const LayoutRenderDetailsList = withLayoutRenderComplete()(ScrollPositionDetailsList);

/**
 * Bind the rows and groups in the details list to an item set.
 */
const RowsAndGroupsDetailsList = withItemSetRowsAndGroups()(LayoutRenderDetailsList);

/**
 * Add support for a 'columnSelector' prop in case the caller needs to assign columns dynamically.
 */
const ColumnsDetailsList = withColumnSelector()(RowsAndGroupsDetailsList);

/**
 * Component which binds the items, pages, and groups within an item set (for a given itemKey)
 * to the rows and groups of a Fabric DetailsList.
 *
 * This component does not provide default columns, renderers, or selection behavior other
 * than the default from Fabric. Use additional decorators to implement full scenarios.
 */
export const ItemSetDetailsList: React.ComponentType<IItemSetDetailsListProps> = withDisplayName('ItemSetDetailsList')(ColumnsDetailsList);
